/**
 * FormControls — themed form primitives shared across the New UI.
 *
 * The native <select> and <input type="date"> render OS-default UI that clashes
 * with the app theme, so these provide fully custom, on-theme replacements:
 *   • Field      — label + required marker + spacing wrapper
 *   • TextField  — polished text input (also exported as `inputClass`)
 *   • Select     — custom dropdown: themed trigger + portal popup panel with
 *                  toned hover rows, a checkmark on the selected option, and
 *                  type-to-filter (shown when the list is long)
 *   • DateField  — custom date: themed trigger + portal mini-calendar popup
 *                  (day/month/year drill-down, today dot, themed selected day).
 *                  Value is the ISO "YYYY-MM-DD" string, matching
 *                  <input type="date">.
 *
 * Popups render in a portal positioned against the trigger rect so they are
 * never clipped by a scrolling modal body; they close on outside-click,
 * scroll, and Escape.
 */
import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiCheck, FiCalendar, FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';
// Shared with DatePicker so "23/07/2026", "23-7-26" and "2026-07-23" are read
// identically wherever a date is typed.
import { parseLooseDate } from './DatePicker';

export const labelClass = 'block text-[12px] font-bold uppercase tracking-[0.6px] text-[#7a7060] mb-1.5';

// Teal focus glow via box-shadow (the Tailwind `ring` utility defaults to a
// blue ring color, which bled through — box-shadow keeps it reliably teal).
const FOCUS = 'focus:border-[#145f5f] focus:bg-white focus:shadow-[0_0_0_3px_rgba(20,95,95,0.18)]';

export const inputClass =
  'w-full rounded-xl border-[1.5px] border-[#ddd6c6] bg-white px-3.5 h-[42px] text-[13px] text-[#08111f] ' +
  'outline-none transition-all duration-200 placeholder:text-[#b3a995] hover:border-[#cdc4b0] ' + FOCUS;

export const textareaClass =
  'w-full rounded-xl border-[1.5px] border-[#ddd6c6] bg-white px-3.5 py-2.5 text-[13px] text-[#08111f] leading-relaxed ' +
  'outline-none transition-all duration-200 placeholder:text-[#b3a995] hover:border-[#cdc4b0] resize-y min-h-[84px] ' + FOCUS;

// Shared trigger chrome so Select/DateField match TextField. `size` switches
// between the default form size and a compact size for dense table rows.
const triggerBase = (size = 'md') =>
  'group w-full flex items-center gap-2 border-[1.5px] bg-white text-left outline-none transition-all duration-200 active:scale-[0.98] ' +
  (size === 'sm' ? 'h-[34px] px-2.5 text-[12px] rounded-lg' : 'h-[42px] px-3.5 text-[13px] rounded-xl');

// Compact input class for dense table cells.
export const inputSmClass =
  'w-full rounded-lg border-[1.5px] border-[#ddd6c6] bg-white px-2.5 h-[34px] text-[12px] text-[#08111f] ' +
  'outline-none transition-all duration-200 placeholder:text-[#b3a995] hover:border-[#cdc4b0] ' + FOCUS;
const triggerOpen = 'border-[#145f5f] shadow-[0_0_0_3px_rgba(20,95,95,0.18)] bg-white';
// Hover lives on the idle state only, so an open trigger keeps its teal border.
const triggerIdle = 'border-[#ddd6c6] hover:border-[#cdc4b0]';

const PANEL_STYLE = {
  border: '1px solid rgba(222,214,198,0.9)',
  boxShadow: '0 16px 40px -8px rgba(8,17,31,0.20), 0 4px 12px -4px rgba(8,17,31,0.10)',
};

/* ── Field wrapper ──────────────────────────────────────────────────────── */
export function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      {label && (
        <label className={labelClass}>
          {label}{required && <span className="text-[#9a564a]"> *</span>}
        </label>
      )}
      {children}
    </div>
  );
}

export function TextField(props) {
  return <input className={inputClass} {...props} />;
}

/* ── Anchored portal position (against trigger rect) ────────────────────── */
// `exactWidth` pins the popup to a set width instead of stretching it to the
// trigger. A calendar has fixed geometry — a 7-column grid stretched across a
// half-modal field reads as a broken overlay, not a date picker.
function useAnchor(open, triggerRef, { estHeight = 300, minWidth = 0, exactWidth = 0 } = {}) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const GAP = 6;   // breathing room between trigger and panel
      const EDGE = 8;  // keep-off margin from the viewport edges
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const below = vh - r.bottom - GAP - EDGE;
      const above = r.top - GAP - EDGE;
      // Flip the panel above the trigger when there isn't room below.
      const openUp = below < estHeight && above > below;
      // Popup is at least `minWidth` wide (narrow table-cell triggers would
      // otherwise truncate the options) and never overflows the viewport edge.
      const width = exactWidth || Math.max(r.width, minWidth);
      let left = r.left;
      if (left + width > vw - EDGE) left = Math.max(EDGE, vw - EDGE - width);
      setPos({
        left,
        width,
        openUp,
        // Space actually available on the chosen side — a panel taller than
        // this scrolls internally rather than running off-screen.
        maxHeight: Math.max(180, openUp ? above : below),
        top: openUp ? undefined : r.bottom + GAP,
        bottom: openUp ? vh - r.top + GAP : undefined,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, triggerRef, estHeight, minWidth, exactWidth]);
  return pos;
}

// Close the popup on outside-click and on any scroll outside the panel.
function useDismiss(open, close, triggerRef, panelRef) {
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      close();
    };
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close, triggerRef, panelRef]);
}

/* ── Custom Select ──────────────────────────────────────────────────────── */
export function Select({ value, onChange, options = [], placeholder = '— Select —', disabled, searchThreshold = 6, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const pos = useAnchor(open, triggerRef, { estHeight: 290, minWidth: 240 });

  // Normalise to {value,label}; ensure the current value is always selectable
  // even if it isn't in the provided list (MCA can return free-text values).
  const opts = useMemo(() => {
    const norm = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    if (value && !norm.some((o) => o.value === value)) norm.push({ value, label: value });
    return norm;
  }, [options, value]);

  const selected = opts.find((o) => o.value === value);
  const showSearch = opts.length > searchThreshold;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
  }, [opts, query]);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  useDismiss(open, close, triggerRef, panelRef);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => (showSearch ? searchRef.current : panelRef.current)?.focus(), 0);
  }, [open, showSearch]);

  useEffect(() => {
    const idx = filtered.findIndex((o) => o.value === value);
    setHi(idx >= 0 ? idx : 0);
  }, [filtered, value, open]);

  const choose = (v) => { onChange(v); close(); triggerRef.current?.focus(); };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((p) => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((p) => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) choose(filtered[hi].value); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); triggerRef.current?.focus(); }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`${triggerBase(size)} ${open ? triggerOpen : triggerIdle} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className={`flex-1 truncate transition-transform duration-150 group-active:translate-y-[0.5px] ${selected ? 'text-[#08111f]' : 'text-[#b3a995]'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <FiChevronDown
          size={15}
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-[#145f5f]' : 'text-[#7a7060]'}`}
        />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          tabIndex={-1}
          onKeyDown={onKey}
          className={`fixed z-[1100] rounded-xl bg-white overflow-hidden outline-none animate-in fade-in zoom-in-95 duration-150 ${pos.openUp ? 'slide-in-from-bottom-1' : 'slide-in-from-top-1'}`}
          style={{ left: pos.left, width: pos.width, ...(pos.openUp ? { bottom: pos.bottom } : { top: pos.top }), ...PANEL_STYLE }}
        >
          {showSearch && (
            <div className="flex items-center gap-2 px-3 h-[38px] border-b" style={{ borderColor: '#eee6d8' }}>
              <FiSearch size={13} className="text-[#9a9080] shrink-0" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKey}
                placeholder="Filter…"
                className="flex-1 bg-transparent outline-none text-[12.5px] text-[#08111f] placeholder:text-[#b3a995]"
              />
            </div>
          )}
          <div className="max-h-[248px] overflow-y-auto cs-menu-scroll p-1 flex flex-col gap-1" style={{ scrollbarGutter: 'auto' }}>
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-[#9a9080] text-center">No matches</div>
            )}
            {filtered.map((o, i) => {
              const isSel = o.value === value;
              const isHi = i === hi;
              return (
                <button
                  type="button"
                  key={o.value || `opt-${i}`}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => choose(o.value)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-[12.5px] transition-colors"
                  style={{
                    background: isHi ? '#faf7f2' : 'transparent',
                    color: isSel ? '#145f5f' : '#08111f',
                    fontWeight: isSel ? 600 : 500,
                  }}
                >
                  <span className="flex-1 truncate">{o.label}</span>
                  {isSel && <FiCheck size={14} className="shrink-0 text-[#145f5f]" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/* ── Custom DateField ───────────────────────────────────────────────────── */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
function fmtDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${pad(d)} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
}

/** ISO "YYYY-MM-DD" → "DD/MM/YYYY" for the typeable trigger. */
function toDMY(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '';
}

/**
 * Hold typed input to a date shape: digits only, separators normalised to "/",
 * progressively formatted as DD/MM/YYYY. Letters never make it into the field —
 * it used to accept any string ("fdsfds fdsxcxzz") and only reject it on blur.
 *
 * Typed separators are honoured rather than re-derived, so "8/5/1995" stays
 * "8/5/1995" (and expands to 08/05/1995 on commit) instead of being re-grouped
 * from its digits into a nonsense "85/19/95".
 */
function maskDMY(raw) {
  const s = String(raw ?? '')
    .replace(/[^\d/\-.\s]/g, '') // letters never enter the field
    .replace(/[-.\s]/g, '/')     // one separator to reason about
    .replace(/\/{2,}/g, '/')     // dropped letters must not leave a gap behind
    .replace(/^\//, '');         // ...nor a separator with nothing before it
  if (!/\d/.test(s)) return '';
  if (s.includes('/')) {
    const groups = s.split('/').slice(0, 3);
    const [d = '', m = '', y = ''] = groups;
    let out = d.slice(0, 2);
    if (groups.length > 1) out += `/${m.slice(0, 2)}`;
    if (groups.length > 2) out += `/${y.slice(0, 4)}`;
    return out;
  }
  // No separator typed yet — insert them as the digits arrive.
  const dg = s.slice(0, 8);
  if (dg.length <= 2) return dg;
  if (dg.length <= 4) return `${dg.slice(0, 2)}/${dg.slice(2)}`;
  return `${dg.slice(0, 2)}/${dg.slice(2, 4)}/${dg.slice(4)}`;
}

// Calendar geometry: a fixed-width panel keeps the 7-column grid square.
const CAL_WIDTH = 268;
const YEAR_BLOCK = 12;

/** One selectable cell in the day / month / year grids. */
function CalCell({ active, onClick, children }) {
  const idle = () => ({ color: '#08111f', background: 'transparent' });
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-8 grid place-items-center rounded-lg text-[12px] font-medium transition-colors"
      style={active ? { background: '#145f5f', color: '#fff', fontWeight: 700 } : idle()}
      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = '#e4f5f3'; e.currentTarget.style.color = '#145f5f'; } }}
      onMouseLeave={(e) => { if (!active) Object.assign(e.currentTarget.style, idle()); }}
    >
      {children}
    </button>
  );
}

/**
 * Themed date input: type DD/MM/YYYY directly, or pick from the calendar.
 *
 * The trigger used to be a read-only button, which meant a date could ONLY be
 * chosen by clicking through the calendar — no typing, no pasting. Users asked
 * for manual entry, so the display span is now a real text input, held to a
 * DD/MM/YYYY mask (see maskDMY) so prose can never be typed into a date field.
 * Clicking anywhere in the box opens the calendar; the panel takes no focus, so
 * typing and picking stay available at the same time.
 *
 * The panel keeps a fixed CAL_WIDTH rather than stretching to the trigger: on a
 * half-modal field (Date of Incorporation) a trigger-width calendar rendered as
 * a ~400px slab that blanketed the form when it flipped above the field. It is
 * also clamped to the space available on the side it opens, so it can never run
 * off-screen.
 *
 * The header month and year are each clickable, opening a month grid and a
 * 12-year grid: stepping the arrows one month at a time is the only way to move
 * otherwise, and an incorporation date decades back is then unreachable.
 */
export function DateField({ value, onChange, placeholder = 'Select date', size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  // Which grid the panel shows: days, the 12 months, or a 12-year block. The
  // header's month and year each jump straight to their own grid.
  const [mode, setMode] = useState('day');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const pos = useAnchor(open, triggerRef, { estHeight: 330, exactWidth: CAL_WIDTH });

  // Mirror the upstream value, but never while the user is mid-type — that
  // would rewrite the digits under their cursor.
  useEffect(() => {
    if (!focused) setText(toDMY(value));
  }, [value, focused]);

  // Escape must discard, not save. blur() runs commitText synchronously, and
  // that closure still holds the pre-Escape text — so the revert is flagged on
  // a ref the blur handler checks before committing.
  const cancelRef = useRef(false);

  // Commit on blur/Enter. An unparseable string is rejected rather than
  // half-saved, and the field snaps back to the last good value.
  const commitText = () => {
    setFocused(false);
    const raw = text.trim();
    if (!raw) {
      if (value) onChange('');
      return;
    }
    const parsed = parseLooseDate(raw);
    if (parsed) onChange(`${parsed.year}-${parsed.month}-${parsed.day}`);
    else setText(toDMY(value));
  };

  const handleBlur = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      setFocused(false);
      setText(toDMY(value));
      return;
    }
    commitText();
  };

  const today = new Date();
  const todayIso = isoOf(today.getFullYear(), today.getMonth(), today.getDate());

  const sel = value ? new Date(`${value}T00:00:00`) : null;
  const selValid = sel && !Number.isNaN(sel.getTime());

  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });

  // Jump the calendar to the selected month each time it opens, always at day level.
  useEffect(() => {
    if (!open) return;
    setMode('day');
    if (selValid) setView({ y: sel.getFullYear(), m: sel.getMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, triggerRef, panelRef);

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const pick = (d) => { onChange(isoOf(view.y, view.m, d)); close(); };

  // The arrows and the header label both follow the current zoom level.
  const blockStart = Math.floor(view.y / YEAR_BLOCK) * YEAR_BLOCK;
  const step = (dir) => setView((v) => {
    if (mode === 'day') {
      const m = v.m + dir;
      if (m < 0) return { y: v.y - 1, m: 11 };
      if (m > 11) return { y: v.y + 1, m: 0 };
      return { y: v.y, m };
    }
    return { ...v, y: v.y + dir * (mode === 'month' ? 1 : YEAR_BLOCK) };
  });
  // Header chrome: the month and the year are separately clickable, so year
  // selection is one click away rather than buried behind a zoom-out sequence.
  const headerBtn = 'px-2 py-0.5 rounded-lg text-[12.5px] font-bold text-[#08111f] hover:bg-[#faf7f2] hover:text-[#145f5f] transition-colors';

  return (
    <>
      {/* Clicking anywhere in the box opens the calendar — the icon alone was
          too small a target for what reads as a picker. Typing still works: the
          panel never takes focus away from the input. */}
      <div
        ref={triggerRef}
        onClick={() => setOpen(true)}
        className={`${triggerBase(size)} ${open || focused ? triggerOpen : triggerIdle} cursor-pointer`}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          title="Pick from calendar"
          aria-label="Open calendar"
          className="shrink-0 grid place-items-center"
        >
          <FiCalendar size={14} className={open ? 'text-[#145f5f]' : 'text-[#7a7060]'} />
        </button>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          value={text}
          onChange={(e) => {
            const masked = maskDMY(e.target.value);
            setText(masked);
            // Follow the typing with the open calendar once it reads as a date.
            const p = parseLooseDate(masked);
            if (p) setView({ y: Number(p.year), m: Number(p.month) - 1 });
          }}
          // A pasted "23 July 2026" / "2026-07-23" is parsed whole rather than
          // run through the numeric mask, which would mangle it.
          onPaste={(e) => {
            const p = parseLooseDate(e.clipboardData?.getData('text'));
            if (!p) return;
            e.preventDefault();
            setText(`${p.day}/${p.month}/${p.year}`);
            onChange(`${p.year}-${p.month}-${p.day}`);
          }}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); close(); e.currentTarget.blur(); }
            if (e.key === 'Escape') {
              // Keep Escape scoped to the calendar while it's open — Modal
              // listens on window and would otherwise close the whole form.
              if (open) e.stopPropagation();
              cancelRef.current = true;
              close();
              e.currentTarget.blur();
            }
          }}
          placeholder={focused ? 'DD/MM/YYYY' : placeholder}
          aria-label={placeholder}
          className="flex-1 min-w-0 bg-transparent border-0 p-0 outline-none text-[inherit] text-[#08111f] placeholder:text-[#b3a995] cursor-pointer focus:cursor-text"
        />
        {selValid && !focused && (
          <span className="shrink-0 text-[#a39a88] text-[11px] hidden sm:inline">{fmtDisplay(value)}</span>
        )}
        {selValid && (
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange(''); setText(''); }}
            className="shrink-0 text-[#b3a995] hover:text-[#9a564a] text-[15px] leading-none px-0.5"
            title="Clear date"
          >
            ×
          </button>
        )}
      </div>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          className={`fixed z-[1100] rounded-xl bg-white p-3 overflow-y-auto cs-menu-scroll animate-in fade-in zoom-in-95 duration-150 ${pos.openUp ? 'slide-in-from-bottom-1' : 'slide-in-from-top-1'}`}
          style={{ left: pos.left, width: pos.width, maxHeight: pos.maxHeight, ...(pos.openUp ? { bottom: pos.bottom } : { top: pos.top }), ...PANEL_STYLE }}
        >
          {/* Nav — arrows step the current unit; month/year open their own grids */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label={mode === 'day' ? 'Previous month' : mode === 'month' ? 'Previous year' : 'Previous years'}
              className="w-7 h-7 grid place-items-center rounded-lg text-[#7a7060] hover:bg-[#faf7f2] transition-colors"
            >
              <FiChevronLeft size={16} />
            </button>

            <div className="flex items-center gap-0.5">
              {mode === 'day' && (
                <button type="button" onClick={() => setMode('month')} title="Change month" className={headerBtn}>
                  {MONTHS[view.m]}
                </button>
              )}
              {mode !== 'year' ? (
                <button type="button" onClick={() => setMode('year')} title="Change year" className={headerBtn}>
                  {view.y}
                </button>
              ) : (
                <button type="button" onClick={() => setMode('day')} title="Back to days" className={headerBtn}>
                  {blockStart} – {blockStart + YEAR_BLOCK - 1}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => step(1)}
              aria-label={mode === 'day' ? 'Next month' : mode === 'month' ? 'Next year' : 'Next years'}
              className="w-7 h-7 grid place-items-center rounded-lg text-[#7a7060] hover:bg-[#faf7f2] transition-colors"
            >
              <FiChevronRight size={16} />
            </button>
          </div>

          {mode === 'day' && (
            <>
              {/* Weekday header */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="h-6 grid place-items-center text-[9.5px] font-bold uppercase text-[#a39a88]">{w}</div>
                ))}
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((d, i) => {
                  if (d === null) return <div key={`e${i}`} />;
                  const iso = isoOf(view.y, view.m, d);
                  const isSel = iso === value;
                  const isToday = iso === todayIso;
                  return (
                    <CalCell key={iso} active={isSel} onClick={() => pick(d)}>
                      <span className="relative">
                        {d}
                        {isToday && !isSel && <span className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#145f5f]" />}
                      </span>
                    </CalCell>
                  );
                })}
              </div>
            </>
          )}

          {mode === 'month' && (
            <div className="grid grid-cols-3 gap-1">
              {MONTHS.map((mn, i) => (
                <CalCell
                  key={mn}
                  active={selValid && sel.getFullYear() === view.y && sel.getMonth() === i}
                  onClick={() => { setView((v) => ({ ...v, m: i })); setMode('day'); }}
                >
                  {mn.slice(0, 3)}
                </CalCell>
              ))}
            </div>
          )}

          {mode === 'year' && (
            <div className="grid grid-cols-3 gap-1">
              {Array.from({ length: YEAR_BLOCK }, (_, i) => blockStart + i).map((y) => (
                <CalCell
                  key={y}
                  active={selValid && sel.getFullYear() === y}
                  onClick={() => { setView((v) => ({ ...v, y })); setMode('month'); }}
                >
                  {y}
                </CalCell>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: '#eee6d8' }}>
            <button type="button" onClick={() => { onChange(todayIso); close(); }} className="text-[11px] font-semibold text-[#145f5f] hover:underline">Today</button>
            <button type="button" onClick={() => { onChange(''); close(); }} className="text-[11px] font-semibold text-[#9a564a] hover:underline">Clear</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
